import { motion } from "framer-motion";
import Container from "../global/container";
import { cn } from "@/lib";

interface SectionProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  className?: string;
  containerClassName?: string;
  spacing?: "small" | "medium" | "large" | "xlarge";
  maxWidth?: "small" | "medium" | "large" | "xlarge" | "full";
  background?: "transparent" | "card" | "gradient";
  animated?: boolean;
  id?: string;
}

const Section = ({
  children,
  title,
  subtitle,
  className,
  containerClassName,
  spacing = "large",
  maxWidth = "large",
  background = "transparent",
  animated = true,
  id,
}: SectionProps) => {
  const getSpacingClasses = () => {
    switch (spacing) {
      case "small":
        return "py-12";
      case "medium":
        return "py-16";
      case "xlarge":
        return "py-24";
      default:
        return "py-20";
    }
  };

  const getMaxWidthClasses = () => {
    switch (maxWidth) {
      case "small":
        return "max-w-4xl";
      case "medium":
        return "max-w-5xl";
      case "xlarge":
        return "max-w-7xl";
      case "full":
        return "max-w-full";
      default:
        return "max-w-6xl";
    }
  };

  const getBackgroundClasses = () => {
    switch (background) {
      case "card":
        return "bg-card border border-border rounded-2xl p-8";
      case "gradient":
        return "bg-gradient-to-r from-blue-50/50 to-purple-50/50 border border-border/50 rounded-2xl p-8";
      default:
        return "";
    }
  };

  return (
    <section
      id={id}
      className={cn(
        "relative flex flex-col items-center justify-center w-full",
        getSpacingClasses(),
        className,
      )}
      data-oid="oo84:6b"
    >
      <Container
        className={cn(getMaxWidthClasses(), "mx-auto", containerClassName)}
        data-oid="o0116bx"
      >
        <div className={getBackgroundClasses()} data-oid="m-x3k0_">
          {/* Section Header */}
          {(title || subtitle) && (
            <motion.div
              className="text-center mb-12"
              initial={animated ? { opacity: 0, y: 20 } : {}}
              whileInView={animated ? { opacity: 1, y: 0 } : {}}
              transition={animated ? { duration: 0.6 } : {}}
              viewport={{ once: true }}
              data-oid=".ut_h.x"
            >
              {title && (
                <motion.h2
                  className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
                  initial={animated ? { opacity: 0, y: 20 } : {}}
                  whileInView={animated ? { opacity: 1, y: 0 } : {}}
                  transition={animated ? { duration: 0.6, delay: 0.1 } : {}}
                  viewport={{ once: true }}
                  data-oid="8qti19q"
                >
                  {title}
                </motion.h2>
              )}
              {subtitle && (
                <motion.p
                  className="text-lg text-muted-foreground max-w-2xl mx-auto"
                  initial={animated ? { opacity: 0, y: 20 } : {}}
                  whileInView={animated ? { opacity: 1, y: 0 } : {}}
                  transition={animated ? { duration: 0.6, delay: 0.2 } : {}}
                  viewport={{ once: true }}
                  data-oid="2rcd.tz"
                >
                  {subtitle}
                </motion.p>
              )}
            </motion.div>
          )}

          {/* Section Content */}
          <motion.div
            initial={animated ? { opacity: 0, y: 20 } : {}}
            whileInView={animated ? { opacity: 1, y: 0 } : {}}
            transition={
              animated
                ? { duration: 0.6, delay: title || subtitle ? 0.3 : 0 }
                : {}
            }
            viewport={{ once: true }}
            data-oid="n:38s8j"
          >
            {children}
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

export default Section;
